import * as React from 'react';

import {
  Button,
  DataGrid,
  ListScreenLayout,
  Pagination,
  useDataGridSelection,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import { $openManageAdminAccessDialog } from '../../_state/manageAdminAccessDialog.state';
import { $openManageUserOrganizationsDialog } from '../../_state/manageUserOrganizationsDialog.state';
import {
  $openCreateUserDialog,
  $openEditUserDialog,
} from '../../_state/userDialog.state';
import * as state from '../../_state/userList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { UserRecord } from '../../types';

import { useUserListColumns } from './hooks/useUserListColumns';
import { ManageAdminAccessDialog } from './ManageAdminAccessDialog';
import { ManageUserOrganizationsDialog } from './ManageUserOrganizationsDialog';
import { UserDetailsDialog } from './UserDetailsDialog';
import { UserListFilterBar } from './UserListFilterBar';

//
// ⚛️ ⚛️ ⚛️ USERS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const UsersListView = () => {
  const t = useTranslations();

  const [_selectedRows, setSelectedRows] = React.useState<UserRecord[]>([]);

  // Atoms
  const selectedOrganizationId = useAtomValue(state.$selectedOrganizationId);
  const {
    data: results,
    isFetching,
    loadingState,
    error,
  } = useAtomValue(state.$users);
  const [limit, setLimit] = useAtom(state.$limit);
  const [offset, setOffset] = useAtom(state.$offset);
  const openEditDialog = useSetAtom($openEditUserDialog);
  const openCreateDialog = useSetAtom($openCreateUserDialog);
  const openManageOrganizationsDialog = useSetAtom(
    $openManageUserOrganizationsDialog,
  );
  const openManageAdminAccessDialog = useSetAtom($openManageAdminAccessDialog);

  // Memoized values
  const users = React.useMemo(() => results?.users ?? [], [results?.users]);
  const total = results?.total ?? 0;
  const columns = useUserListColumns({
    openEditDialog,
    openManageOrganizationsDialog,
    openManageAdminAccessDialog,
  });
  const handleSelectionChange = useDataGridSelection(users, setSelectedRows);

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const emptyPanelProps = React.useMemo(
    () => ({
      title: selectedOrganizationId
        ? t.userListView.empty.title
        : t.userListView.empty.selectOrganization,
      description: selectedOrganizationId
        ? t.userListView.empty.description
        : t.userListView.empty.selectOrganizationDescription,
    }),
    [selectedOrganizationId, t],
  );

  return (
    <ListScreenLayout
      filters={<UserListFilterBar />}
      actions={
        <Button
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
          onPress={openCreateDialog}
        >
          {t.userListView.actions.create}
        </Button>
      }
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
      className="@UsersListView"
    >
      <DataGrid
        columns={columns}
        dataSource={users}
        fullWidth
        isLoading={loadingState.isInitialLoading || isFetching}
        rowKey="id"
        selectionMode="multiple"
        onSelectionChange={handleSelectionChange}
        hasError={!!error}
        errorPanelProps={
          error ? { description: error.message, centered: true } : undefined
        }
        emptyPanelProps={emptyPanelProps}
        className="w-full text-sm"
      />
      <UserDetailsDialog />
      <ManageUserOrganizationsDialog />
      <ManageAdminAccessDialog />
    </ListScreenLayout>
  );
};
